/**
 * useWorkspaceMemberHandlers Hook
 * 
 * Handles member management operations:
 * - Invite user
 * - Resend invitation
 * - Remove member
 * - Change member role
 */

import React from "react";
import { Alert } from "@mui/material";
import { db } from "../../../firebase";
import { doc, deleteDoc, updateDoc, setDoc, getDoc, collection, getDocs, writeBatch, query, where, serverTimestamp } from "firebase/firestore";
import { WorkspaceMemberWithDetails, WorkspaceRole } from "../../../types";
import { api } from "../../../services/api";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

export interface UseWorkspaceMemberHandlersParams {
  workspaceId: string;
  isOwner: boolean;
  currentUserRole: WorkspaceRole;
  workspaceOwnerId: string | null;
  canManageMembers: () => boolean;
  members: WorkspaceMemberWithDetails[];
  invitations: Array<{ id: string; email: string; status: string }>;
  setInviteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setInviteEmail: React.Dispatch<React.SetStateAction<string>>;
  setInviteRole: React.Dispatch<React.SetStateAction<WorkspaceRole>>;
  setInviting: React.Dispatch<React.SetStateAction<boolean>>;
  setInviteError: React.Dispatch<React.SetStateAction<string>>;
  setCreatedInvitationLink: React.Dispatch<React.SetStateAction<string | null>>;
  setInvitationSuccessDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailSent: React.Dispatch<React.SetStateAction<boolean>>;
  setEmailError: React.Dispatch<React.SetStateAction<{ type: string; message: string } | undefined>>;
  setRemoveMemberDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMemberToRemove: React.Dispatch<React.SetStateAction<WorkspaceMemberWithDetails | null>>;
  setRemoving: React.Dispatch<React.SetStateAction<boolean>>;
  setChangingRole: React.Dispatch<React.SetStateAction<string | null>>;
  setResendingInvite: React.Dispatch<React.SetStateAction<string | null>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  validateEmail: (email: string) => { valid: boolean; error?: string };
  inviteEmail: string;
  inviteRole: WorkspaceRole;
  memberToRemove: WorkspaceMemberWithDetails | null;
}

export interface UseWorkspaceMemberHandlersReturn {
  handleInviteUser: () => Promise<void>;
  handleResendInvitation: (invitationId: string) => Promise<void>;
  handleRemoveMember: () => Promise<void>;
  handleRoleChange: (memberId: string, newRole: WorkspaceRole) => Promise<void>;
  handleCleanupOrphanedMembers: () => Promise<{ removed: number; orphaned: WorkspaceMemberWithDetails[]; transactionsAssigned: number; userDocsDeleted: number }>;
}

/**
 * Hook for member management handlers
 */
export function useWorkspaceMemberHandlers({
  workspaceId,
  isOwner,
  currentUserRole,
  workspaceOwnerId,
  canManageMembers,
  members,
  invitations,
  setInviteDialogOpen,
  setInviteEmail,
  setInviteRole,
  setInviting,
  setInviteError,
  setCreatedInvitationLink,
  setInvitationSuccessDialogOpen,
  setEmailSent,
  setEmailError,
  setRemoveMemberDialogOpen,
  setMemberToRemove,
  setRemoving,
  setChangingRole,
  setResendingInvite,
  setSnackbarOpen,
  setSnackbarElement,
  validateEmail,
  inviteEmail,
  inviteRole,
  memberToRemove,
}: UseWorkspaceMemberHandlersParams): UseWorkspaceMemberHandlersReturn {
  const handleInviteUser = async (): Promise<void> => {
    // DEMO MODE: Block write operations in production, but allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Users cannot be invited. Please log in to invite users." />
      );
      setSnackbarOpen(true);
      setInviteDialogOpen(false);
      return;
    }

    if (!isOwner && !canManageMembers()) {
      setSnackbarElement(
        <Alert severity="warning">You don't have permission to invite members.</Alert>
      );
      setSnackbarOpen(true);
      return;
    }

    // Validate email
    const emailValidation = validateEmail(inviteEmail);
    if (!emailValidation.valid) {
      setInviteError(emailValidation.error || "Email is required");
      return;
    }

    // Check if user is already a member
    const existingMember = members.find((m) => m.email?.toLowerCase() === inviteEmail.trim().toLowerCase());
    if (existingMember) {
      setInviteError("User is already a member of this workspace");
      return;
    }

    // Check if invitation already exists
    const existingInvitation = invitations.find(
      (inv) => inv.email.toLowerCase() === inviteEmail.trim().toLowerCase()
    );
    if (existingInvitation) {
      setInviteError("Invitation already sent to this email");
      return;
    }

    setInviting(true);
    setInviteError("");

    try {
      // Call API to send invitation (this will create the invitation and send email)
      // inviteRole is typed as WorkspaceRole which includes custom roles
      const result = await api.sendInvitation(workspaceId, inviteEmail.trim(), inviteRole);

      // Generate invitation link
      const frontendUrl = window.location.origin;
      const invitationLink = `${frontendUrl}/invite/${result.id}`;
      setCreatedInvitationLink(invitationLink);
      setEmailSent(result.emailSent ?? false);
      setEmailError(result.emailError);

      setInviteEmail("");
      setInviteRole("user"); // Reset to default
      setInviteDialogOpen(false);
      setInvitationSuccessDialogOpen(true);
    } catch (error) {
      console.error("Error sending invitation:", error);
      let errorMessage = "Failed to send invitation. Please try again.";
      
      if (error instanceof Error) {
        errorMessage = error.message;
        // Check if it's a network error
        if (error.message.includes("Failed to fetch") || error.message.includes("Cannot connect")) {
          errorMessage = "Cannot connect to server. Please make sure the backend is running.";
        }
      }
      
      setInviteError(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (invitationId: string): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Invitations cannot be resent. Please log in to resend invitations." />
      );
      setSnackbarOpen(true);
      return;
    }

    setResendingInvite(invitationId);
    try {
      // Call API to resend invitation (this will update expiration and send email)
      await api.resendInvitation(workspaceId, invitationId);
    } catch (error) {
      console.error("Error resending invitation:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to resend invitation";
      alert(errorMessage);
    } finally {
      setResendingInvite(null);
    }
  };

  const handleRemoveMember = async (): Promise<void> => {
    if (!memberToRemove) return;
    // Owner can remove anyone; others need manageMembers and cannot remove the workspace owner
    const isTargetOwner = workspaceOwnerId != null && memberToRemove.id === workspaceOwnerId;
    const canRemove = isOwner || (canManageMembers() && !isTargetOwner);
    if (!canRemove) return;

    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Members cannot be removed. Please log in to remove members." />
      );
      setSnackbarOpen(true);
      setRemoveMemberDialogOpen(false);
      setMemberToRemove(null);
      return;
    }

    setRemoving(true);
    try {
      // Check if it's an invitation or an active member
      if (memberToRemove.status === "invited") {
        // Delete invitation
        await deleteDoc(doc(db, "workspaces", workspaceId, "invitations", memberToRemove.id));
      } else {
        // Delete member
        await deleteDoc(doc(db, "workspaces", workspaceId, "members", memberToRemove.id));
      }
      
      setRemoveMemberDialogOpen(false);
      setMemberToRemove(null);
    } catch (error) {
      console.error("Error removing member:", error);
      alert("Failed to remove member");
    } finally {
      setRemoving(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: WorkspaceRole): Promise<void> => {
    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: User roles cannot be changed. Please log in to change user roles." />
      );
      setSnackbarOpen(true);
      return;
    }

    // Allow role changes in bypass auth mode (dev mode) - needed to promote users to owner
    const isBypassMode = isBypassAuthEnabled();
    
    // Allow if user is owner (primary or via role), admin, OR in bypass auth mode
    const canChangeRoles = isOwner || currentUserRole === "owner" || currentUserRole === "admin" || isBypassMode;
    if (!workspaceId || !canChangeRoles) {
      console.warn("Cannot change role: isOwner=", isOwner, "currentUserRole=", currentUserRole, "isBypassMode=", isBypassMode);
      if (!isBypassMode) {
        setSnackbarElement(
          <Alert severity="warning">
            You don't have permission to change roles. Only owners and admins can change member roles.
          </Alert>
        );
        setSnackbarOpen(true);
      }
      return;
    }

    setChangingRole(memberId);
    try {
      const memberRef = doc(db, "workspaces", workspaceId, "members", memberId);
      await updateDoc(memberRef, {
        role: newRole,
      });
      console.log(`✅ Successfully updated member ${memberId} role to ${newRole}`);
    } catch (error) {
      console.error("Error updating member role:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setSnackbarElement(
        <Alert severity="error">
          Failed to update member role: {errorMessage}
        </Alert>
      );
      setSnackbarOpen(true);
    } finally {
      setChangingRole(null);
    }
  };

  /**
   * Cleanup orphaned members - members whose user documents don't exist
   * This helps clean up "Unknown User" entries in workspaces
   * 
   * Before deleting orphaned members, assigns all their transactions to Bypass-User
   * This ensures no transactions are lost when cleaning up orphaned users
   * Note: We use bypass-user instead of dev-user-developer now
   */
  const handleCleanupOrphanedMembers = async (): Promise<{ removed: number; orphaned: WorkspaceMemberWithDetails[]; transactionsAssigned: number; userDocsDeleted: number }> => {
    // Allow owners, demo mode users, or bypass auth users (dev mode)
    if (!isOwner && !isDemoMode() && !isBypassAuthEnabled()) {
      throw new Error("Only workspace owners can clean up orphaned members");
    }

    // DEMO MODE: Block in production, allow in development
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Cannot clean up members. Please log in to clean up orphaned members." />
      );
      setSnackbarOpen(true);
      throw new Error("Demo mode: Cannot clean up members");
    }

    // Bypass-user ID - used to assign transactions before deleting orphaned members
    // Note: We use bypass-user instead of dev-user-developer now
    const BYPASS_USER_ID = "bypass-user";

    // Step 1: Find or create Bypass-User in members
    let bypassUserExists = false;
    try {
      const bypassUserMemberRef = doc(db, "workspaces", workspaceId, "members", BYPASS_USER_ID);
      const bypassUserMemberSnap = await getDoc(bypassUserMemberRef);
      bypassUserExists = bypassUserMemberSnap.exists();
      
      if (!bypassUserExists) {
        // Try to create Bypass-User
        try {
          await setDoc(bypassUserMemberRef, {
            uid: BYPASS_USER_ID,
            name: "Bypass-User",
            displayName: "Bypass-User",
            email: "bypass-user@dev-workspace.local",
            role: "user",
            joinedAt: serverTimestamp(),
          });
          bypassUserExists = true;
          console.log("Created Bypass-User for transaction assignment");
        } catch (createError: any) {
          // If we can't create it, we'll still try to assign transactions
          // but log a warning
          console.warn("Could not create Bypass-User, will still try to assign transactions:", createError);
        }
      }
    } catch (error) {
      console.warn("Error checking/creating Bypass-User:", error);
    }

    const orphanedMembers: WorkspaceMemberWithDetails[] = [];
    
    // Step 2: Query members collection directly to find ALL members (not just ones in current list)
    // This ensures we catch orphaned users that might not be in the members array
    let allMembersSnapshot;
    try {
      const membersRef = collection(db, "workspaces", workspaceId, "members");
      allMembersSnapshot = await getDocs(membersRef);
      console.log(`[Cleanup] Found ${allMembersSnapshot.docs.length} members in collection`);
      // Log all member IDs for debugging
      allMembersSnapshot.docs.forEach(doc => {
        console.log(`[Cleanup] Member ID: ${doc.id}`);
      });
    } catch (error) {
      console.error("Error fetching members collection for cleanup:", error);
      // Fall back to using the members array passed in
      allMembersSnapshot = null;
    }
    
    // Use members from snapshot if available, otherwise use passed-in members array
    const membersToCheck = allMembersSnapshot 
      ? allMembersSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name as string | undefined,
          email: doc.data().email as string | undefined,
          role: (doc.data().role as string | undefined) || "user",
          joinedAt: doc.data().joinedAt,
          status: "active" as const,
        }))
      : members;
    
    // Get workspace owner ID to skip it
    let workspaceOwnerId: string | undefined;
    try {
      const workspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
      if (workspaceDoc.exists()) {
        workspaceOwnerId = workspaceDoc.data()?.ownerId as string | undefined;
      }
    } catch (error) {
      console.warn("Error fetching workspace owner:", error);
    }
    
    // Step 3: Check each member to see if their user document exists
    for (const member of membersToCheck) {
      // Skip owner - never remove owner
      if (member.role === "owner" || member.id === workspaceOwnerId) {
        continue;
      }

      // Skip Bypass-User - never remove Bypass-User
      if (member.id === BYPASS_USER_ID) {
        continue;
      }

      // Check if user document exists and has valid data
      let userExists = false;
      let userHasData = false;
      try {
        const userDoc = await getDoc(doc(db, "users", member.id));
        if (userDoc.exists()) {
          userExists = true;
          const userData = userDoc.data();
          // Check if user document has meaningful data (name, email, or displayName)
          userHasData = !!(userData?.name || userData?.email || userData?.displayName);
        }
      } catch (error) {
        // If we can't access the user document, assume it doesn't exist
        userExists = false;
        userHasData = false;
      }

      // Explicitly check for known orphaned user IDs
      const KNOWN_ORPHANED_IDS = ["Ao7fhBrt"];
      if (KNOWN_ORPHANED_IDS.includes(member.id)) {
        console.log(`[Cleanup] Explicitly marking as orphaned (known orphaned ID): ${member.id}`);
        orphanedMembers.push(member as WorkspaceMemberWithDetails);
        continue;
      }
      
      // More aggressive orphaned detection:
      // 1. User doc doesn't exist AND member has no name/email
      // 2. User doc exists but is empty AND member has no name/email
      // 3. User doc exists but is empty (regardless of member data) - this catches cases like "Ao7fhBrt"
      const isOrphaned = 
        (!userExists && !member.name && !member.email) || 
        (userExists && !userHasData && !member.name && !member.email) ||
        (userExists && !userHasData); // Most aggressive: if user doc exists but is empty, consider orphaned
      
      if (isOrphaned) {
        orphanedMembers.push(member as WorkspaceMemberWithDetails);
        console.log(`[Cleanup] Detected orphaned member: ${member.id} (userExists: ${userExists}, userHasData: ${userHasData}, memberName: ${member.name}, memberEmail: ${member.email})`);
      }
    }

    // Step 4: For each orphaned member, assign their transactions to Developer
    // Also check for specific known orphaned user IDs even if not in members collection
    const KNOWN_ORPHANED_IDS = ["Ao7fhBrt"];
    for (const orphanedId of KNOWN_ORPHANED_IDS) {
      // Check if this user has any transactions that need to be reassigned
      try {
        const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
        const transactionsQuery = query(transactionsRef, where("userId", "==", orphanedId));
        const transactionsSnapshot = await getDocs(transactionsQuery);
        const transactions = transactionsSnapshot.docs;
        
        if (transactions.length > 0) {
          console.log(`[Cleanup] Found ${transactions.length} transactions for known orphaned user: ${orphanedId}`);
          // Add to orphaned members if not already there
          const alreadyInList = orphanedMembers.find(m => m.id === orphanedId);
          if (!alreadyInList) {
            orphanedMembers.push({
              id: orphanedId,
              role: "user",
              status: "active",
            } as WorkspaceMemberWithDetails);
          }
        }
      } catch (error) {
        console.warn(`[Cleanup] Error checking transactions for ${orphanedId}:`, error);
      }
    }
    
    let totalTransactionsAssigned = 0;
    const BATCH_SIZE = 500; // Firestore batch limit
    
    for (const orphanedMember of orphanedMembers) {
      try {
        // Find all transactions for this orphaned user
        const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
        const transactionsQuery = query(transactionsRef, where("userId", "==", orphanedMember.id));
        const transactionsSnapshot = await getDocs(transactionsQuery);
        const transactions = transactionsSnapshot.docs;
        
        if (transactions.length === 0) {
          console.log(`No transactions found for orphaned member: ${orphanedMember.id}`);
          continue;
        }

        console.log(`Found ${transactions.length} transactions for orphaned member: ${orphanedMember.id}`);
        
        // Update transactions in batches
        for (let i = 0; i < transactions.length; i += BATCH_SIZE) {
          const batch = writeBatch(db);
          const batchTransactions = transactions.slice(i, i + BATCH_SIZE);
          
          for (const transactionDoc of batchTransactions) {
            const transactionRef = doc(
              db,
              "workspaces",
              workspaceId,
              "transactions",
              transactionDoc.id
            );
            batch.update(transactionRef, {
              userId: BYPASS_USER_ID,
            });
          }

          await batch.commit();
          totalTransactionsAssigned += batchTransactions.length;
          console.log(`Assigned batch of ${batchTransactions.length} transactions to Bypass-User`);
        }
        
        console.log(`✅ Assigned ${transactions.length} transactions from ${orphanedMember.id} to Bypass-User`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error(`Error assigning transactions for orphaned member ${orphanedMember.id}:`, errorMessage);
        // Continue with deletion even if transaction assignment fails
      }
    }

    // Step 5: Remove orphaned members and their user documents
    let removed = 0;
    let userDocsDeleted = 0;
    const failedRemovals: Array<{ memberId: string; error: string }> = [];
    
    for (const orphanedMember of orphanedMembers) {
      try {
        // Delete member document
        await deleteDoc(doc(db, "workspaces", workspaceId, "members", orphanedMember.id));
        removed++;
        console.log(`Successfully removed orphaned member: ${orphanedMember.id}`);
        
        // Also delete the user document if it exists (complete cleanup)
        try {
          const userDocRef = doc(db, "users", orphanedMember.id);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            await deleteDoc(userDocRef);
            userDocsDeleted++;
            console.log(`Successfully deleted user document: ${orphanedMember.id}`);
          }
        } catch (userDocError: any) {
          // Log but don't fail - user doc might not exist or might have permission issues
          console.warn(`Could not delete user document for ${orphanedMember.id}:`, userDocError);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error(`Error removing orphaned member ${orphanedMember.id}:`, error);
        failedRemovals.push({ memberId: orphanedMember.id, error: errorMessage });
      }
    }

    // Log summary
    if (totalTransactionsAssigned > 0 || userDocsDeleted > 0) {
      console.log(`✨ Cleanup complete: Assigned ${totalTransactionsAssigned} transactions to Developer, removed ${removed} orphaned member(s), deleted ${userDocsDeleted} user document(s)`);
    } else {
      console.log(`✨ Cleanup complete: Removed ${removed} orphaned member(s)`);
    }

    // If there were failed removals, throw an error with details
    if (failedRemovals.length > 0) {
      const errorMessage = `Failed to remove ${failedRemovals.length} member(s). Errors: ${failedRemovals.map(f => `${f.memberId}: ${f.error}`).join(", ")}`;
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    return { removed, orphaned: orphanedMembers, transactionsAssigned: totalTransactionsAssigned, userDocsDeleted };
  };

  return {
    handleInviteUser,
    handleResendInvitation,
    handleRemoveMember,
    handleRoleChange,
    handleCleanupOrphanedMembers,
  };
}
